/**
 * Cross browser/node requests for Ajax Endpoints.
 *
 * @requires 'axios'
 */
import Axios from 'axios';
import {CONFIG, global} from '../../globals/config';

let {globalNonce} = CONFIG.endpoint;
const {ajaxURL} = CONFIG.endpoint;


type Data = {
	action?: string;
	_ajax_nonce?: string; // eslint-disable-line camelcase
	[ key: string ]: Object | string | undefined;
}

type Response = {
	success: boolean;
	data: Object | string;
}

export function setGlobalNonce( nonce: string ) {
	globalNonce = nonce;
}

export function getGlobalNonce() {
	return globalNonce;
}

/**
 * To send regular form data, it must be turned into form data
 * otherwise it will send as JSON.
 *
 * @param  data
 */
export const encodeForm = ( data ): FormData => {
	const form = new FormData();
	// phpcs:disable WordPressVIPMinimum.JS.HTMLExecutingFunctions.append
	Object.keys( data ).forEach( key => {
		if ( Array.isArray( data[ key ] ) ) {
			if ( 0 === data[ key ].length ) {
				form.append( key + '[]', '' );
			}
			data[ key ].map( v => form.append( key + '[]', v ) );
		} else {
			form.append( key, data[ key ] );
		}
	} );
	// phpcs:enable WordPressVIPMinimum.JS.HTMLExecutingFunctions.append
	return form;
};

/**
 * Send an AJAX post request.
 *
 * @param {string} action   - Ajax Action.
 * @param {Data}   postData - Data to Send.
 * @param {string} nonce    - Defaults to globalNonce
 */
export const post = async <T>( action: string, postData: Data, nonce: string = globalNonce ): Promise<T> => {
	postData.action = action;
	postData._ajax_nonce = nonce;
	if ( global.__TEST__ ) {
		postData._jest_request = true;
	}

	const {data} = await Axios.post<Response>( ajaxURL, encodeForm( postData ) );
	if ( ! data.success ) {
		throw new Error( data.data as string );
	}
	return data.data as T;
};

/**
 * Send an AJAX get request.
 *
 * @param {string} action
 * @param {string} nonce       - Defaults to globalNonce
 * @param {Data}   requestData - Optionally append url params to request
 */
export const get = async <T>( action: string, nonce: string = globalNonce, requestData: { [ key: string ]: string } = {} ): Promise<T> => {
	const url = new URL( ajaxURL );
	url.searchParams.set( 'action', action );
	url.searchParams.set( '_ajax_nonce', nonce );
	Object.entries( requestData ).forEach( ( [ key, value ] ) => {
		url.searchParams.set( key, encodeURIComponent( value ) );
	} );

	const {data} = await Axios.get<Response>( url.toString() );
	if ( ! data.success ) {
		throw new Error( data.data as string );
	}
	return data.data as T;
};
